import { h, Component } from 'preact';
import cx from 'classnames';
import style from './style';
import { labelToScaledTimeInterval, labelToTimeInterval } from '../../lib/intervals';
import { SMALL_BREAKPOINT } from '../dynamic-stock-chart/constants';
import { intlDateNode, shallowDiffers } from '../../lib/util';
import { Select, Option } from '@cube/blocks';
import { financeScaleInvert } from '../../lib/finance-time-scale';
import { Text, Localizer } from 'preact-i18n';

const INTL_NS = 'interval-selector';

/**
 * A radio button list of common time intervals for viewing data on a stock chart
 * @param {Object} props
 * @param {Boolean} scaled [optional, default=true] Whether to use financeScale scaled values (true) or regular Date (false)
 * @param {Function} [props.onSelect]          called when an interval is selected
 * @param {Number[]} [props.customInterval]    a custom time range, an array with the first elemnt being the start time, second element the end time
 * @param {String} [props.class]               class for the container element
 * @param {String|Object} [props.style]        style string or object for the container element
 */
// TODO: Add dynamic interval after static intervals
export default class IntervalSelector extends Component {

	state = { active: 0 };

	formatInterval = (interval) => {
		let start = this.props.scaled ? financeScaleInvert(interval[0]) : interval[0];
		let end = this.props.scaled ? financeScaleInvert(interval[1]) : interval[1];
		return [intlDateNode(start), ' - ', intlDateNode(end)];
	};

	handleSelect = (interval, index, props=this.props) => {
		let label = 'custom';

		if (!Array.isArray(interval)) {
			const to = props.timeLimits && props.timeLimits[1];
			label = interval;
			interval = (props.scaled ? labelToScaledTimeInterval : labelToTimeInterval)(interval.value || interval, to);

		}

		this.setState({ active: index, interval });
		props.onSelect && props.onSelect( interval, label );
	};

	static defaultProps = {
		labels: [ '1d' ],
		scaled: true
	};

	componentWillReceiveProps(nextProps) {
		if (this.props.customInterval !== nextProps.customInterval) {
			if (nextProps.customInterval.length) {
				this.handleSelect(nextProps.customInterval, nextProps.labels.length, nextProps);
			}
		}
		// Default to 1 day view now instead of MAX
		if (this.props.labels.length === 1 && nextProps.labels.length > 1) {
			this.handleSelect(nextProps.labels[0], 0, nextProps);
		}
		// If the 'Max' interval is selected, then set that correctly
		else if (this.state.active === this.props.labels.indexOf('MAX') && shallowDiffers(this.props.labels, nextProps.labels)) {
			this.handleSelect('MAX', nextProps.labels.length - 1, nextProps);
		}
	}

	render({ customInterval, labels, select, displayValue, width, buttonClass, listClass, ...props }, { active, interval }) {
		const intervals = labels.concat(
			width >= SMALL_BREAKPOINT && customInterval && customInterval.length ? [customInterval] : []
		);

		interval = props.interval || interval;

		return (
			<span class={cx(props.class, style['interval-selector'], width <= 375 && style.narrow)} style={props.style}>
				{
					select ?
						<Select
							displayValue={displayValue}
							class={props.class}
							buttonClass={buttonClass}
							listClass={listClass}
							anchor="right"
							disabled={props.disabled}
							value={interval}
							onChange={this.handleSelect}
							iconPosition="right"
						>
							{intervals.map( (label, index) => <Option title={<Text id={`${INTL_NS}.${label}`} />} value={label} />)}
						</Select> :
						!select && intervals.map((label, index) => (
							<Interval
								active={active === index}
								label={label}
								labelFormatter={this.formatInterval}
								index={index}
								onSelect={this.handleSelect}
							/>
						))
				}
			</span>
		);
	}
}

/**
 * A button labelled with a time interval
 * @param {Object} props
 * @param {Function} [props.onSelect]        called when this button is pressed, with props.interval
 * @param {Boolean} [props.active]           if true, this interval is active
 * @param {String|Number[]} [props.label]    label to put on this interval button. if passed a time interval, it will be converted to a label
 * @param {String} [props.class]             class for the container element
 * @param {String|Object} [props.style]      style string or object for the container element
 */
export class Interval extends Component {
	handleClick = () => {
		let { label, index, active } = this.props;
		// no need to fire an event if this button is already active
		!active && this.props.onSelect && this.props.onSelect(label, index);
	};

	render({ active, label, labelFormatter, ...props }) {
		if (Array.isArray(label)) {
			label = labelFormatter(label);
		}
		else {
			let labelKey = label && label.toUpperCase && label.toUpperCase();
			// eslint-disable-next-line no-alert, new-cap
			label = <Text id={`${INTL_NS}.${labelKey}`} />;
		}
		return (
			<Localizer>
				<button
					onClick={this.handleClick}
					class={cx(props.class, style.interval, active && style.active)}
					style={props.style}
				>
					{label}
				</button>
			</Localizer>
		);
	}
}
